import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Star, Quote } from "lucide-react";
import { Reveal, Overline } from "../Reveal";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../ui/tabs";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "../ui/accordion";
import { PROCESS, TESTIMONIALS, FAQS } from "../../data/content";

function ReviewsSlider() {
  const [index, setIndex] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setIndex((v) => (v + 1) % TESTIMONIALS.length), 5000);
    return () => clearInterval(id);
  }, []);
  const t = TESTIMONIALS[index];
  return (
    <div className="mx-auto max-w-2xl text-center">
      <div className="relative min-h-[220px] flex items-center justify-center">
        <Quote className="absolute -top-2 left-1/2 -translate-x-1/2 text-gk-gold/15" size={60} strokeWidth={1} />
        <AnimatePresence mode="wait">
          <motion.div
            key={index}
            data-testid="testimonial-card"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -24 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="glass rounded-3xl p-7 md:p-9 w-full"
          >
            <span className="inline-block mb-4 glass rounded-full px-3 py-1 font-inter text-[10px] tracking-[0.25em] uppercase text-gk-champagne/80">
              Sample Testimonial
            </span>
            <div className="flex justify-center gap-1 mb-5">
              {Array.from({ length: t.rating }).map((_, i) => (
                <Star key={i} size={16} className="fill-gk-gold text-gk-gold" />
              ))}
            </div>
            <p className="font-poppins text-base md:text-lg text-white/80 leading-relaxed font-light">"{t.quote}"</p>
            <div className="mt-6">
              <p className="font-cinzel text-base text-gold-metallic">{t.name}</p>
              <p className="font-inter text-xs tracking-[0.25em] uppercase text-white/50 mt-1">{t.role}</p>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
      <div className="flex justify-center gap-2.5 mt-6">
        {TESTIMONIALS.map((_, i) => (
          <button
            key={i}
            data-testid={`testimonial-dot-${i}`}
            onClick={() => setIndex(i)}
            className={`h-2 rounded-full transition-all duration-400 ${i === index ? "w-8 bg-gk-gold" : "w-2 bg-white/20 hover:bg-white/40"}`}
            aria-label={`Review ${i + 1}`}
          />
        ))}
      </div>
    </div>
  );
}

const triggerClass =
  "rounded-full px-5 md:px-7 py-2.5 font-inter text-xs md:text-sm tracking-wide text-white/60 data-[state=active]:bg-transparent data-[state=active]:text-gk-champagne data-[state=active]:shadow-none transition-colors";

export default function ExploreTabs() {
  return (
    <section id="explore" data-testid="explore-section" className="relative py-16 md:py-24 overflow-hidden">
      <div className="ambient-emerald" style={{ width: 460, height: 460, top: "10%", right: "-12%", opacity: 0.22 }} />

      <div className="relative z-10 mx-auto max-w-5xl px-6 md:px-10">
        <Reveal className="text-center mb-8">
          <Overline>How We Deliver</Overline>
          <h2 className="font-cinzel text-2xl sm:text-3xl lg:text-4xl font-semibold text-white mt-4">
            Process, Reviews <span className="text-gold-metallic">&amp; Answers.</span>
          </h2>
        </Reveal>

        <Reveal delay={0.1}>
          <Tabs defaultValue="process" className="w-full">
            <div className="flex justify-center mb-10">
              <TabsList className="h-auto glass rounded-full p-1.5 gap-1" data-testid="explore-tabs">
                <TabsTrigger value="process" data-testid="tab-process" className={triggerClass}>Our Process</TabsTrigger>
                <TabsTrigger value="reviews" data-testid="tab-reviews" className={triggerClass}>Reviews</TabsTrigger>
                <TabsTrigger value="faq" data-testid="tab-faq" className={triggerClass}>FAQ</TabsTrigger>
              </TabsList>
            </div>

            {/* PROCESS — compact grid */}
            <TabsContent value="process" data-testid="tab-content-process" className="focus-visible:ring-0">
              <div id="process" className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
                {PROCESS.map((p, i) => {
                  const Icon = p.icon;
                  return (
                    <motion.div
                      key={p.step}
                      data-testid={`process-step-${i}`}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5, delay: (i % 4) * 0.06, ease: [0.16, 1, 0.3, 1] }}
                      className="luxe-card rounded-2xl p-4 md:p-5"
                    >
                      <div className="flex items-center justify-between mb-3">
                        <Icon className="text-gk-champagne" strokeWidth={1.3} size={18} />
                        <span className="font-cinzel text-lg text-gk-gold/40">{p.step}</span>
                      </div>
                      <h3 className="font-cinzel text-base text-white mb-1">{p.title}</h3>
                      <p className="font-poppins text-xs text-white/50 leading-relaxed">{p.desc}</p>
                    </motion.div>
                  );
                })}
              </div>
            </TabsContent>

            {/* REVIEWS */}
            <TabsContent value="reviews" data-testid="tab-content-reviews" className="focus-visible:ring-0">
              <ReviewsSlider />
            </TabsContent>

            {/* FAQ */}
            <TabsContent value="faq" data-testid="tab-content-faq" className="focus-visible:ring-0">
              <div id="faq" className="mx-auto max-w-2xl">
                <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
                  {FAQS.map((f, i) => (
                    <AccordionItem key={i} value={`faq-${i}`} data-testid={`faq-item-${i}`} className="border-white/10">
                      <AccordionTrigger className="font-cinzel text-sm md:text-base text-white hover:no-underline hover:text-gk-champagne py-4 [&>svg]:text-gk-gold text-left">
                        {f.q}
                      </AccordionTrigger>
                      <AccordionContent className="font-poppins text-xs md:text-sm text-white/55 leading-relaxed pb-4">
                        {f.a}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            </TabsContent>
          </Tabs>
        </Reveal>
      </div>
    </section>
  );
}
